import type * as CSS from 'csstype';
import type { ComponentPropsWithoutRef, PropsWithChildren } from 'react';
import type { RuleSet } from 'styled-components';
import type { PartialPrefix } from '../../typescript';
import type { TokenFunction } from '../../styling/get-token';
import { css, styled } from 'styled-components';
import { withStyleModifiers } from '../../typescript';

interface SpaceBetweenProps extends ComponentPropsWithoutRef<'div'> {
  direction: 'x' | 'y';
  spacing?: string | TokenFunction;
  /** CSS property: 'align-items' */
  alignItems?: CSS.Property.AlignItems;
  /** CSS property: 'justify-content' */
  justifyContent?: CSS.Property.JustifyContent;
  padding?: string | TokenFunction;
}
type SpaceBetweenStyledProps = PartialPrefix<
  SpaceBetweenProps,
  'direction' | 'spacing' | 'alignItems' | 'justifyContent' | 'padding'
>;

function SpaceBetweenComponent({
  alignItems,
  direction,
  spacing,
  justifyContent,
  padding,
  children,
  ...attributes
}: PropsWithChildren<SpaceBetweenProps>): JSX.Element {
  return (
    <StyledSpaceBetween
      $spacing={spacing}
      $direction={direction}
      $alignItems={alignItems}
      $justifyContent={justifyContent}
      $padding={padding}
      {...attributes}
    >
      {children}
    </StyledSpaceBetween>
  );
}

SpaceBetweenComponent.displayName = 'SpaceBetween';

const StyledSpaceBetween = styled.div<SpaceBetweenStyledProps>`
  ${(props) => {
    switch (props.$direction) {
      case 'x':
        return css`
          flex-direction: row;
          > * ~ * {
            margin-left: ${props.$spacing ?? 'initial'} !important;
          }
        `;
      case 'y':
        return css`
          flex-direction: column;
          > * ~ * {
            margin-top: ${props.$spacing ?? 'initial'} !important;
          }
        `;
    }
  }}
  display: flex;
  align-items: ${(props) => props.$alignItems};
  justify-content: ${(props) => props.$justifyContent};
  padding: ${(props) => props.$padding};
`;

/**
 * Generates a CSS rule set for spacing between elements, based on the direction. This can be triggered by the parent component, to apply different spacing based on responsive breakpoints.
 *
 * @param spacing - The spacing between elements.
 * @param direction - The direction of the spacing. Defaults to 'x'.
 * @returns - The generated CSS rule set.
 *
 * @example
 * ```css
 * // Usage:
 * ${SpaceBetween} {
 *  ${SpaceBetween.modAlignment(tk('spacing_md'))};
 * }
 * ```
 */
const modAlignment = (
  spacing: SpaceBetweenProps['spacing'],
  direction: SpaceBetweenProps['direction'] = 'x'
): RuleSet<object> => css`
  ${() => {
    return css`
      > * ~ * {
        ${direction === 'x' ? 'margin-left' : 'margin-top'}: ${spacing} !important;
      }
    `;
  }}
`;

export const SpaceBetween = withStyleModifiers(styled(SpaceBetweenComponent)``, {
  modAlignment,
});
